import * as React from "react";

import { AsyncComponent } from "@impero-com/common/components/asyncComponent";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { withIdParams } from "@impero-com/common/components/routerUtils";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import withI18n, { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    emitNotification,
    FormFrame,
    FormBox,
    PageSpinner,
} from "@impero-com/ui";
import { Spin } from "antd";
import { isEqual } from "lodash-es";
import memoizeOne from "memoize-one";
import { Link } from "react-router-dom";

import {
    UserGroupViewDto as UserGroupWithDetails,
    UserGroupView,
    UserGroupWithDetailsAccessRights,
    UpdateUserGroupBody,
} from "api/generated/model";
import {
    getUserGroupsGroupId,
    patchUserGroupsGroupId,
} from "api/generated/user-groups/user-groups";
import { ACCESS_RIGHTS } from "utils/accessRights/constants";
import {
    UserAndRights,
    UserGroupAccessRights,
} from "utils/accessRights/models";

import { WebRoute } from "../../../routes";

import { UserGroupDetailsEditor } from "./editor";
import { UserGroupsApplicationBar, USER_GROUP_DETAILS_TAB } from "./manager";

interface UserGroupDetailsProps {
    ajaxManager: AjaxManager;
    id: string;
}

type UserGroupEditorStatus = { type: "DEFAULT" } | { type: "SUBMITTING" };

interface UserGroupDetailsState {
    status: UserGroupEditorStatus;
}

const convertAccessRightsToEditor = memoizeOne(
    (
        userGroupResource: UserGroupWithDetails
    ): Array<UserAndRights<UserGroupAccessRights>> => {
        const {
            linkedResources: { users, accessRights },
        } = userGroupResource;
        const rights: Array<UserAndRights<UserGroupAccessRights>> = [];
        accessRights.edit.forEach((userId) => {
            const user = users.find((u) => u.resource.id === userId)?.resource;
            if (user !== undefined) {
                rights.push({ user, accessRight: ACCESS_RIGHTS.EDIT });
            }
        });
        accessRights.admin.forEach((userId) => {
            const user = users.find((u) => u.resource.id === userId)?.resource;
            if (user !== undefined) {
                rights.push({ user, accessRight: ACCESS_RIGHTS.ADMIN });
            }
        });
        return rights;
    }
);

const convertAccessRightsToBackend = memoizeOne(
    (
        rights: Array<UserAndRights<UserGroupAccessRights>>
    ): UserGroupWithDetailsAccessRights => {
        const edit = rights
            .filter((r) => r.accessRight === ACCESS_RIGHTS.EDIT)
            .map((r) => r.user.id);
        const admin = rights
            .filter((r) => r.accessRight === ACCESS_RIGHTS.ADMIN)
            .map((r) => r.user.id);
        return { edit, admin };
    }
);

class UserGroupDetailsComp extends React.PureComponent<
    UserGroupDetailsProps,
    UserGroupDetailsState
> {
    state: UserGroupDetailsState = { status: { type: "DEFAULT" } };
    asyncComponent = React.createRef<AsyncComponent<UserGroupWithDetails>>();

    render = () => {
        const { ajaxManager, id: userGroupId } = this.props;

        return (
            <AsyncComponent<UserGroupWithDetails>
                ajaxManager={ajaxManager}
                promise={() => getUserGroupsGroupId(Number(userGroupId))}
                ref={this.asyncComponent}
                renderLoading={() => <PageSpinner />}
                renderError={(code) => {
                    return (
                        <UserGroupsApplicationBar
                            currentTabId={USER_GROUP_DETAILS_TAB}
                        >
                            <LoggedInHTTPError code={code} />
                        </UserGroupsApplicationBar>
                    );
                }}
                key={userGroupId}
                renderContent={(userGroupWithDetails) => {
                    const {
                        content: { resource: userGroup, actions },
                    } = userGroupWithDetails;
                    const accessRights =
                        convertAccessRightsToEditor(userGroupWithDetails);
                    const editable = actions.admin || actions.edit;

                    if (!editable) {
                        return (
                            <UserGroupsApplicationBar
                                userGroup={userGroup}
                                currentTabId={USER_GROUP_DETAILS_TAB}
                            >
                                <FormFrame>
                                    <NoAccessCard />
                                </FormFrame>
                            </UserGroupsApplicationBar>
                        );
                    }

                    return (
                        <UserGroupsApplicationBar
                            userGroup={userGroup}
                            currentTabId={USER_GROUP_DETAILS_TAB}
                        >
                            <FormFrame>
                                <Spin spinning={this.showSpinner()}>
                                    <FormBox>
                                        <UserGroupDetailsEditor
                                            type="EDIT"
                                            onChangeName={(name) =>
                                                this.updateUserGroup(
                                                    userGroup,
                                                    { name: name?.trim() }
                                                )
                                            }
                                            onChangeDescription={(
                                                description
                                            ) =>
                                                this.updateUserGroup(
                                                    userGroup,
                                                    {
                                                        description:
                                                            description?.trim(),
                                                    }
                                                )
                                            }
                                            onChangeAccessRights={(
                                                accessRights
                                            ) =>
                                                this.updateUserGroup(
                                                    userGroup,
                                                    {
                                                        accessRights:
                                                            convertAccessRightsToBackend(
                                                                accessRights
                                                            ),
                                                    }
                                                )
                                            }
                                            userGroup={userGroup}
                                            accessRights={accessRights}
                                            canEditAccessRights={actions.admin}
                                        />
                                        <Link
                                            to={WebRoute.USER_GROUPS}
                                            className="ant-btn ant-btn-primary"
                                        >
                                            {t("UserManagement_Group_Close")}
                                        </Link>
                                    </FormBox>
                                </Spin>
                            </FormFrame>
                        </UserGroupsApplicationBar>
                    );
                }}
            />
        );
    };

    showSpinner(): boolean {
        const { status } = this.state;
        switch (status.type) {
            case "SUBMITTING":
                return true;
            case "DEFAULT":
                return false;
            default:
                assertNever(status);
        }
    }

    updateUserGroup(
        currentUserGroup: UserGroupView,
        value: UpdateUserGroupBody
    ): Promise<void> {
        const userGroupId = this.props.id;
        const ajaxManager = this.props.ajaxManager;

        // Skip PATCH request if no changes were made
        const newUserGroup = { ...currentUserGroup, ...value };
        if (isEqual(currentUserGroup, newUserGroup)) {
            return Promise.resolve();
        }

        return ajaxManager
            .ajax<UserGroupWithDetails, UserGroupEditorStatus>(
                () => patchUserGroupsGroupId(Number(userGroupId), value),
                {
                    component: this,
                    initialStatusValue: "DEFAULT",
                    inFlightStatus: { type: "SUBMITTING" },
                    getSuccessStatus: (_resp) => ({ type: "DEFAULT" }),
                    getErrorStatus: (_code) => ({ type: "DEFAULT" }),
                }
            )
            .then((userGroupWithDetails) => {
                this.asyncComponent.current?.updateData(userGroupWithDetails);
                emitNotification(
                    "success",
                    t("User_Groups_Update_Message_Success")
                );
            });
    }
}

const UserGroupDetails = withIdParams(
    withI18n<UserGroupDetailsProps>(UserGroupDetailsComp)
);

export { UserGroupDetails };
